import { Box } from '@mui/system';
import React, { useEffect, useMemo } from 'react';
import { CustomSideNavigation } from '@biq/apex-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { CollapsedLifegraphLogo } from '../../../../assets';
import { SINGLE_NAVIGATION_PATHS } from '../../../../constants/single-navigation-paths';
import { METADATA_STORAGE_KEY } from '../../../../pages/metadata-search/metadata-search-helpert';
import {
    pinnedSideNavItems,
    sideNavItems,
    styles,
} from './custom-side-nav-helper';
import { useAuth } from 'core/contexts';
interface SideNavigationProps {
    handleDrawerToggle: () => void;
    onVersionModalOpen: () => void;
    onHelpDeskModalOpen: () => void;
}

interface TabItem {
    icon?: React.JSX.Element;
    isActive: boolean;
    title?: string;
    path: string;
    isDisabled?: boolean;
    group?: string;
    showDivider?: boolean;
    isNewTabLink?: boolean;
}

interface GroupItem {
    groupIcon: React.JSX.Element;
    groupName: string;
    tabItems?: TabItem[];
    showDivider?: boolean;
}

const SideNavigation = ({
    handleDrawerToggle,
    onVersionModalOpen,
    onHelpDeskModalOpen,
}: SideNavigationProps): React.JSX.Element => {
    const [tabItems, setTabItems] = React.useState(sideNavItems);
    const navigate = useNavigate();
    const location = useLocation();
    const { hasRequiredRoles } = useAuth();

    const getFilteredTabItems = useMemo(() => {
        const hasAccess = (item: any): boolean => {
            if ('requiredRoles' in item) {
                return (
                    !item.requiredRoles?.length ||
                    hasRequiredRoles(item?.requiredRoles ?? [])
                );
            }
            return true;
        };

        const filterTabItems = (items: any): TabItem[] | GroupItem[] => {
            return items.filter((item: any) => {
                if ('tabItems' in item) {
                    item.tabItems = filterTabItems(item.tabItems);
                    return item.tabItems.length > 0;
                }
                return hasAccess(item);
            });
        };

        const cleanItem = (item: any): any => {
            const newItem = { ...item };
            delete newItem.requiredRoles;
            if ('tabItems' in newItem) {
                newItem.tabItems = newItem.tabItems.map(cleanItem);
            }
            return newItem;
        };

        const filterAndCleanItems = (items: any): any => {
            return filterTabItems(items).map(cleanItem);
        };

        return filterAndCleanItems(tabItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabItems]);

    const setActiveTabByPath = (path: string): any => {
        return tabItems.map((tabItem: any): TabItem[] | GroupItem[] => {
            if ('path' in tabItem && path.includes(tabItem.path)) {
                return { ...tabItem, isActive: true };
            } else if ('tabItems' in tabItem && tabItem.tabItems) {
                const updatedTabItems = tabItem.tabItems.map(
                    (subTabItem: any): TabItem[] | GroupItem[] => ({
                        ...subTabItem,
                        isActive:
                            subTabItem.title === 'About'
                                ? false
                                : path.includes(subTabItem.path),
                    })
                );
                return { ...tabItem, tabItems: updatedTabItems };
            } else {
                return { ...tabItem, isActive: false };
            }
        });
    };

    useEffect(() => {
        setTabItems(setActiveTabByPath(location.pathname));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleButtonClick = (path: string): void => {
        /**
         * This should allow us open any modal from the SideNav when we don't need to open the component using a route.
         * If we need to display a modal via the path, then you should move this logic to custom-side-nav-helper
         * and add the component for that specific route.
         *
         * NOTE: If we need a new modal change the logic to a switch statement. Currently, SonarLint complains
         * if we have less than 3 case clauses.
         */

        if (path === SINGLE_NAVIGATION_PATHS.ABOUT_APP_VERSION) {
            onVersionModalOpen();
        } else if (path === SINGLE_NAVIGATION_PATHS.HELP_DESK) {
            onHelpDeskModalOpen();
        } else {
            setTabItems(setActiveTabByPath(path));
            navigate(path);
        }

        /**
         * Remove possibly saved metadata search if user interacts with any
         * SideNavigation item and there's saved metadata search in local storage.
         */
        if (localStorage.getItem(METADATA_STORAGE_KEY)) {
            localStorage.removeItem(METADATA_STORAGE_KEY);
        }
    };

    return (
        <Box>
            <CustomSideNavigation
                tabItems={getFilteredTabItems}
                onNavItemClick={handleButtonClick}
                onHamburgerIconClick={handleDrawerToggle}
                sx={styles.sideNavStyles}
                navLogo={CollapsedLifegraphLogo}
                pinItemsAtBottom
                pinnedTabItems={pinnedSideNavItems()}
            />
        </Box>
    );
};

export default SideNavigation;
